import RecordedChampion from '../../components/RecordedChampion.jsx'
import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import ChampionshipDrawer from '../command-center/ChampionshipDrawer.jsx'
import { normalizeChampionship } from '../../lib/championships.js'
import { activityOf, activityIndex, gapsFor, isIncomplete, ACTIVITY_PRESETS, NO_BODY } from '../../lib/activities.js'
import { searchChampionships, SCOPES } from '../../lib/championshipSearch.js'
import { matcherFor, isDefectiveName } from '../../lib/bodyChampionships.js'
import styles from './ChampionshipFinder.module.css'

/* =========================================================================
 * FIND A CHAMPIONSHIP — BY NAME, BY SPORT, IN ONE BODY OR IN THE WHOLE
 * UNIVERSE.
 *
 * Brian: "if I want to find every football championship within a governing
 * body or within our entire universe, I should be able to do so."
 *
 * ⚠️ SEARCH IS THE MECHANISM. THE PRESETS ARE SHORTCUTS INTO IT.
 * The previous version of this drawer was an activity lens: eight curated
 * categories, and nothing else reachable. That is a menu, not a search, and it
 * answered a question somebody chose in advance rather than the one being
 * asked. The free-text box is now the primitive and the chips below it just
 * fill it in.
 *
 * ⚠️ THE GOVERNING-BODY CARDS ARE STILL NOT TOUCHED BY ANY OF THIS.
 * A Georgia cheer title is crowned by GHSA. Nothing here moves a record onto
 * another body, and nothing here writes: `sr_conference` is read and shown,
 * never set. The cards stay the true picture of what each body crowns.
 *
 * ⚠️ AND NOTHING IS WITHHELD FOR BEING INCOMPLETE.
 * Brian: "There cannot be any invisible sports… flag it and say that it isn't
 * complete, so we can get it rectified, because every championship needs to be
 * pursued." Every matching record renders, and the ones missing something
 * render WITH the gap named on the row. `complete + flagged = total` is stated
 * on screen and pinned in verify-activity-lens.mjs.
 * ========================================================================= */

const S = (v) => String(v ?? '').trim()
const nf = (n) => Number(n || 0).toLocaleString()

const bodyOf = (r) => S(r?.raw?.sr_conference ?? r?.sr_conference)
const bodyLabel = (r) => bodyOf(r) || NO_BODY
const stateOf = (r) => S(r?.raw?.sr_state_location ?? r?.sr_state_location) || S(r?.raw?.sr_state ?? r?.sr_state) || ''
const levelOf = (r) => S(r?.raw?.sr_level ?? r?.sr_level) || ''
const nameOf = (r) => S(r?.name ?? r?.raw?.championship_name ?? r?.championship_name)
const idOf = (r) => String(r?.id || r?.raw?.id || r?.raw?.hs_object_id || r?.hs_object_id || '')

const PAGE = 400

export default function ChampionshipFinder({
  universe = [],
  entity = null,
  initialQuery = '',
  initialActivity = '',
  initialScope = 'universe',
  onClose,
}) {
  const [q, setQ] = useState(initialQuery)
  const [scope, setScope] = useState(entity ? initialScope : 'universe')
  const [activity, setActivity] = useState(initialActivity)
  const [body, setBody] = useState('')
  const [level, setLevel] = useState('')
  const [only, setOnly] = useState('')          // '' | 'flagged' | 'clean'
  const [limit, setLimit] = useState(PAGE)
  const [record, setRecord] = useState(null)

  /* ⚠️ THE BODY SCOPE USES THE SAME MATCHER THE CARD USES. A search that
     scoped by a different rule than the card counts by would give two numbers
     for one body, and the person reading them would have no way to tell which
     was wrong. */
  const matcher = useMemo(
    () => (scope === 'body' && entity ? matcherFor(entity) : null),
    [scope, entity],
  )

  const found = useMemo(
    () => searchChampionships(universe, q, { matcher }),
    [universe, q, matcher],
  )

  const index = useMemo(() => activityIndex(found.rows), [found.rows])

  const rows = useMemo(() => found.rows.filter((r) => {
    if (activity && activityOf(r).activity !== activity) return false
    if (body && bodyLabel(r) !== body) return false
    if (level && (levelOf(r) || '(no level)') !== level) return false
    if (only === 'flagged' && !isIncomplete(r)) return false
    if (only === 'clean' && isIncomplete(r)) return false
    return true
  }), [found.rows, activity, body, level, only])

  const axes = useMemo(() => {
    const bodies = new Map(); const levels = new Map()
    for (const r of found.rows) {
      bodies.set(bodyLabel(r), (bodies.get(bodyLabel(r)) || 0) + 1)
      const l = levelOf(r) || '(no level)'
      levels.set(l, (levels.get(l) || 0) + 1)
    }
    return {
      bodies: [...bodies].sort((a, b) => b[1] - a[1]),
      levels: [...levels].sort((a, b) => b[1] - a[1]),
    }
  }, [found.rows])

  const flaggedAll = useMemo(() => rows.filter(isIncomplete).length, [rows])
  const cleanAll = rows.length - flaggedAll
  const reconciles = flaggedAll + cleanAll === rows.length
  const shown = rows.slice(0, limit)

  const reset = () => { setActivity(''); setBody(''); setLevel(''); setOnly(''); setLimit(PAGE) }
  const runPreset = (p) => { setQ(''); setActivity(p.activity); setBody(''); setLevel(''); setOnly(''); setLimit(PAGE) }

  return createPortal(
    <div className={styles.scrim} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div>
            <p className={styles.eyebrow}>Find a championship · read-only</p>
            <h2>{scope === 'body' && entity ? `Inside ${entity.acronym || entity.name}` : 'Everything we hold'}</h2>
            <p className={styles.sub}>
              Search by name or by sport across {nf(universe.length)} championships. Every result
              still belongs to the body that crowns it — nothing here moves a record or rewrites
              one.
            </p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        {/* THE PRIMITIVE. */}
        <label className={styles.searchBox}>
          <span>Search championships</span>
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE) }}
            placeholder="football, cheer, wrestling, GHSA, Ohio 4A…" />
        </label>

        {entity && (
          <div className={styles.chips}>
            {SCOPES.map((s) => (
              <button key={s.key} type="button" data-on={scope === s.key ? '1' : '0'}
                onClick={() => { setScope(s.key); reset() }}>
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* ⚠️ SHORTCUTS, NOT THE MENU. Each one just fills the activity in. */}
        <div className={styles.chips}>
          {ACTIVITY_PRESETS.map((p) => (
            <button key={p.key} type="button" data-on={activity === p.activity && !q ? '1' : '0'}
              onClick={() => runPreset(p)} title={p.note || undefined}>
              {p.label}
            </button>
          ))}
          {(activity || q || body || level || only) && (
            <button type="button" onClick={() => { setQ(''); reset() }}>Clear</button>
          )}
        </div>

        {/* ⚠️ FLAG FOOTBALL IS NOT FOOTBALL, AND THE BREAKDOWN SAYS SO BESIDE
            THE TOTAL RATHER THAN BEHIND A DISCLOSURE. A single number labelled
            "football" would report 95 flag-football titles as football to a
            rep about to quote it. */}
        {found.byActivity.length > 0 && (
          <p className={styles.breakdown}>
            <b>{nf(found.total)}</b> {found.total === 1 ? 'match' : 'matches'}
            {q ? <> for “{q}”</> : null} —{' '}
            {found.byActivity.slice(0, 8).map((a, i) => (
              <span key={a.activity}>
                {i > 0 && ' · '}
                <button type="button" className={styles.actBtn}
                  data-on={activity === a.activity ? '1' : '0'}
                  onClick={() => setActivity(activity === a.activity ? '' : a.activity)}>
                  {a.activity} <b>{nf(a.count)}</b>
                </button>
              </span>
            ))}
            {found.byActivity.length > 8 && <> · and {found.byActivity.length - 8} more</>}
          </p>
        )}

        <div className={styles.selects}>
          <label>
            <span>Activity</span>
            <select value={activity} onChange={(e) => { setActivity(e.target.value); setLimit(PAGE) }}>
              <option value="">Every activity ({index.length})</option>
              {index.map((i) => (
                <option key={i.activity} value={i.activity}>
                  {i.activity} — {i.total}{i.incomplete ? ` (${i.incomplete} flagged)` : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Governing body</span>
            <select value={body} onChange={(e) => { setBody(e.target.value); setLimit(PAGE) }}>
              <option value="">Every body ({axes.bodies.length})</option>
              {axes.bodies.map(([b, n]) => <option key={b} value={b}>{b} — {n}</option>)}
            </select>
          </label>
          <label>
            <span>Level</span>
            <select value={level} onChange={(e) => { setLevel(e.target.value); setLimit(PAGE) }}>
              <option value="">Every level</option>
              {axes.levels.map(([l, n]) => <option key={l} value={l}>{l} — {n}</option>)}
            </select>
          </label>
        </div>

        {/* ⚠️ THE RECONCILIATION, ON SCREEN. Two numbers that must add to the
            third, where anyone can check them against the list below. */}
        <p className={styles.reconcile} data-bad={reconciles ? '0' : '1'}>
          <b>{nf(rows.length)}</b> showing — <b>{nf(cleanAll)}</b> complete and{' '}
          <b>{nf(flaggedAll)}</b> flagged as missing something.{' '}
          {reconciles
            ? 'Those two add up to the whole result: nothing is filtered out of this view.'
            : '⚠️ Those two do NOT add up — this view is dropping records and must not be trusted.'}
          {found.defective > 0 && (
            <> {nf(found.defective)} of them {found.defective === 1 ? 'is' : 'are'} a truncated
              record whose name is only an acronym, not a championship.</>
          )}
        </p>

        <div className={styles.chips}>
          <button type="button" data-on={only === '' ? '1' : '0'} onClick={() => { setOnly(''); setLimit(PAGE) }}>
            All <b>{nf(found.total)}</b>
          </button>
          <button type="button" data-on={only === 'flagged' ? '1' : '0'} onClick={() => { setOnly('flagged'); setLimit(PAGE) }}>
            Needs work
          </button>
          <button type="button" data-on={only === 'clean' ? '1' : '0'} onClick={() => { setOnly('clean'); setLimit(PAGE) }}>
            Complete
          </button>
        </div>

        <div className={styles.rows}>
          {shown.map((r) => {
            const sp = activityOf(r)
            const gaps = gapsFor(r)
            const broken = isDefectiveName(r)
            return (
              <article key={idOf(r)} className={styles.row} data-flagged={gaps.length || broken ? '1' : '0'}
                role="button" tabIndex={0}
                onClick={() => setRecord(normalizeChampionship(r?.raw || r))}
                onKeyDown={(ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault(); setRecord(normalizeChampionship(r?.raw || r))
                  }
                }}>
                <div className={styles.rowMain}>
                  <span className={styles.name}>Championship: {nameOf(r) || '(unnamed record)'}</span>
                  <RecordedChampion record={r} />
                  <span className={styles.meta}>
                    {[sp.clean || 'no sport recorded', levelOf(r), stateOf(r)].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div className={styles.flags}>
                  <span className={styles.body} data-none={bodyOf(r) && bodyOf(r) !== NO_BODY ? '0' : '1'}>
                    {bodyLabel(r)}
                  </span>
                  {sp.activity && <span className={styles.fam}>{sp.activity}</span>}
                  {sp.discipline && <span className={styles.disc}>{sp.discipline}</span>}
                  {broken && <span className={styles.bad}>name is only an acronym — not a championship</span>}
                  {sp.corrupted && (
                    <span className={styles.bad} title={`stored as ${sp.raw}`}>encoding fault in sport</span>
                  )}
                  {gaps.map((g) => <span key={g} className={styles.warn}>{g}</span>)}
                </div>
              </article>
            )
          })}
          {!rows.length && (
            <p className={styles.none}>
              {q
                ? <>Nothing matches “{q}”. It searches championship names, governing bodies, states,
                  levels and sports — including the repaired spelling and the folded activity, so a
                  record stored as “Spirit — Pom” is still found by “cheer”.</>
                : <>Type a sport or a name above, or pick one of the shortcuts.</>}
            </p>
          )}
        </div>

        {rows.length > shown.length && (
          <p className={styles.more}>
            Showing the first {nf(shown.length)} of {nf(rows.length)}.{' '}
            <button type="button" className={styles.moreBtn} onClick={() => setLimit(limit + PAGE)}>
              Show {nf(Math.min(PAGE, rows.length - shown.length))} more ›
            </button>
          </p>
        )}
      </div>

      {record && <ChampionshipDrawer champ={record} now={Date.now()} onClose={() => setRecord(null)} />}
    </div>,
    document.body,
  )
}
